import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";

export interface NotificationSettings {
  morningReminder: boolean;
  prePrayerAlert: boolean;
  reminderTime: number; // minutes before Jummah
}

export const useNotifications = () => {
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [settings, setSettings] = useState<NotificationSettings>({
    morningReminder: false,
    prePrayerAlert: false,
    reminderTime: 30, // default 30 minutes before
  });
  const { toast } = useToast();

  // Check existing permission on mount
  useEffect(() => {
    if ("Notification" in window) {
      setHasPermission(Notification.permission === "granted");
      
      // Load settings from localStorage
      const savedSettings = localStorage.getItem("jummah-notification-settings");
      if (savedSettings) {
        setSettings(JSON.parse(savedSettings));
      }
    }
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("jummah-notification-settings", JSON.stringify(settings));
  }, [settings]);

  const requestPermission = async () => {
    if (!("Notification" in window)) {
      toast({
        title: "Not Supported",
        description: "Your browser doesn't support notifications.",
        variant: "destructive",
      });
      return false;
    }

    if (Notification.permission === "granted") {
      setHasPermission(true);
      return true;
    }

    if (Notification.permission === "denied") {
      toast({
        title: "Notifications Blocked",
        description: "Please enable notifications in your browser settings.",
        variant: "destructive",
      });
      return false;
    }

    setIsRequesting(true);
    
    try {
      const permission = await Notification.requestPermission();
      setHasPermission(permission === "granted");
      
      if (permission === "granted") {
        toast({
          title: "Notifications Enabled",
          description: "You'll receive prayer reminders before Jummah.",
        });
        
        // Show a test notification
        new Notification("Jummah Finder", {
          body: "Notifications are now enabled! You'll get reminders before Friday prayers.",
          icon: "/favicon.ico",
        });
        
        return true;
      } else {
        toast({
          title: "Permission Denied",
          description: "You can enable notifications later in settings.",
          variant: "destructive",
        });
        return false;
      }
    } catch (error) {
      console.error("Notification permission error:", error);
      toast({
        title: "Error",
        description: "Failed to request notification permission.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsRequesting(false);
    }
  };

  const sendNotification = (title: string, body: string, options?: NotificationOptions) => {
    if (!hasPermission) {
      console.warn("No notification permission");
      return;
    }

    try {
      const notification = new Notification(title, {
        body,
        icon: "/favicon.ico",
        badge: "/favicon.ico",
        requireInteraction: true,
        ...options,
      });

      // Auto-close after 10 seconds if not interacted with
      setTimeout(() => {
        notification.close();
      }, 10000);

      return notification;
    } catch (error) {
      console.error("Failed to send notification:", error);
    }
  };

  const scheduleJummahReminder = (jummahTime: Date, mosqueName?: string) => {
    if (!hasPermission) return;

    const now = new Date();
    const timeUntilJummah = jummahTime.getTime() - now.getTime();
    
    // Don't schedule if Jummah time has already passed
    if (timeUntilJummah <= 0) return;
    
    const reminderTime = settings.reminderTime * 60 * 1000; // Convert to milliseconds
    const scheduleKey = `jummah-${jummahTime.toISOString().split('T')[0]}`;
    
    // Check if we've already scheduled notifications for this Jummah
    const scheduledNotifications = localStorage.getItem('scheduled-notifications');
    const scheduled = scheduledNotifications ? JSON.parse(scheduledNotifications) : {};
    
    if (scheduled[scheduleKey]) {
      return; // Already scheduled for this date
    }

    // Schedule morning reminder (if enabled and it's early enough)
    if (settings.morningReminder && timeUntilJummah > 4 * 60 * 60 * 1000) { // More than 4 hours away
      const morningReminderTime = timeUntilJummah - (4 * 60 * 60 * 1000); // 4 hours before
      
      setTimeout(() => {
        sendNotification(
          "🕌 Jummah Prayer Today",
          `Don't forget about Jummah prayer today at ${jummahTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}${mosqueName ? ` at ${mosqueName}` : '. Find a nearby mosque in the app!'}`
        );
      }, morningReminderTime);
    }

    // Schedule pre-prayer alert (if enabled and there's enough time)
    if (settings.prePrayerAlert && timeUntilJummah > reminderTime) {
      setTimeout(() => {
        sendNotification(
          "⏰ Time to Leave for Jummah!",
          `Jummah prayer starts in ${settings.reminderTime} minutes${mosqueName ? ` at ${mosqueName}` : '. Check your travel time!'}`
        );
      }, timeUntilJummah - reminderTime);
    }
    
    // Mark as scheduled
    scheduled[scheduleKey] = true;
    localStorage.setItem('scheduled-notifications', JSON.stringify(scheduled));
    
    // Clean up old schedule entries after Jummah time passes
    setTimeout(() => {
      const currentScheduled = localStorage.getItem('scheduled-notifications');
      if (currentScheduled) {
        const parsed = JSON.parse(currentScheduled);
        delete parsed[scheduleKey];
        localStorage.setItem('scheduled-notifications', JSON.stringify(parsed));
      }
    }, timeUntilJummah + 60 * 60 * 1000); // Clean up 1 hour after Jummah
  };

  const updateSettings = (newSettings: Partial<NotificationSettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  return {
    hasPermission,
    isRequesting,
    settings,
    requestPermission,
    sendNotification,
    scheduleJummahReminder,
    updateSettings,
  };
};